import { useSyncExternalStore } from 'react'

/** Device-local preferences (no account round-trip needed): text size and Latin transliteration. */

/** localStorage that never throws (private mode, blocked storage). */
export const safe = {
  get: (k: string) => { try { return localStorage.getItem(k) } catch { return null } },
  set: (k: string, v: string) => { try { localStorage.setItem(k, v) } catch { /* private mode: keep in memory only */ } },
  remove: (k: string) => { try { localStorage.removeItem(k) } catch { /* private mode */ } },
}

export type FontScale = 0 | 1 | 2
export const FONT_LABELS = ['Normal', 'Besar', 'Paling besar'] as const

const listeners = new Set<() => void>()
const emit = () => listeners.forEach((l) => l())
const subscribe = (cb: () => void) => { listeners.add(cb); return () => { listeners.delete(cb) } }

let fontScale: FontScale = (() => {
  const n = Number(safe.get('iqp_fs') ?? 0)
  return (n === 1 || n === 2 ? n : 0) as FontScale
})()
let latinOn = safe.get('iqp_latin') !== '0'

/** Arabic text size in the Qur'an reader: 4 steps (26/31/37/44 px at normal text size). */
export const AR_SIZES = ['1.625rem', '1.95rem', '2.3rem', '2.75rem'] as const
let arSize = (() => {
  const n = Number(safe.get('iqp_ar') ?? 1)
  return Number.isInteger(n) && n >= 0 && n < AR_SIZES.length ? n : 1
})()
export function setArSize(n: number) {
  arSize = Math.max(0, Math.min(AR_SIZES.length - 1, n))
  safe.set('iqp_ar', String(arSize))
  emit()
}
export const useArSize = () => useSyncExternalStore(subscribe, () => arSize)

export function applyFontScale(n: FontScale = fontScale) {
  if (n === 0) document.documentElement.removeAttribute('data-fs')
  else document.documentElement.setAttribute('data-fs', String(n))
}

export function setFontScale(n: FontScale) {
  fontScale = n
  safe.set('iqp_fs', String(n))
  applyFontScale(n)
  emit()
}
export function setLatinOn(v: boolean) {
  latinOn = v
  safe.set('iqp_latin', v ? '1' : '0')
  emit()
}

export const useFontScale = () => useSyncExternalStore(subscribe, () => fontScale)
export const useLatinOn = () => useSyncExternalStore(subscribe, () => latinOn)
